import { useElementBounding, useElementSize } from '@vueuse/core'
import { computed, nextTick, watch, type Ref } from 'vue'

/**
 * In charge of handling scroll behavior for case chat UI
 *
 * - computes padding that needs to be added to the chat scroll container so the last query sent can be scrolled to the top of the page
 * - computes padding for the bottom of the chat scroll container so the chat content is never obscured by the input field
 * - implements autoscrolling behavior for the chat, where if the user scrolls to bottom and the content grows, we keep snapped to the bottom
 */
export const useCaseScroll = ({
  containerRef,
  inputRef,
  lastQueryRef,
  lastResponseRef,
}: {
  containerRef: Ref<HTMLElement | null>
  inputRef: Ref<HTMLElement | null>
  lastQueryRef: Ref<HTMLElement | null>
  lastResponseRef: Ref<HTMLElement | null>
}) => {
  const { bottom: containerBottom, height: containerHeight } = useElementBounding(containerRef)
  const { height: lastQueryHeight } = useElementSize(lastQueryRef)
  const { height: lastResponseHeight } = useElementSize(lastResponseRef)

  // we could compute these from DOM, but they are unlikely to change often

  // gap between query and response bubbles (gap-2)
  const BUBBLE_GAP = 8
  // padding at the top of the chat container (pt-8)
  const CONTAINER_PADDING_TOP = 32

  /**
   * Padding given to the chat container so that any query can be scrolled to the top of the page
   */
  const chatPadding = computed(() => {
    const queryResponseHeight = lastResponseRef.value
      ? lastQueryHeight.value + lastResponseHeight.value + BUBBLE_GAP
      : lastQueryHeight.value

    const result =
      containerHeight.value - inputPadding.value - queryResponseHeight - CONTAINER_PADDING_TOP

    return Math.max(0, result)
  })

  const { top: inputTop } = useElementBounding(inputRef)

  /**
   * Padding given to the chat container so that the chat content is never obscured by the input field
   */
  const inputPadding = computed(() => containerBottom.value - inputTop.value)

  // When there is a new response, scroll to it
  watch(lastResponseRef, async (newEl, oldEl) => {
    await nextTick()
    /**
     * Show the last response:
     * 1. At the bottom of the screen when first loading the chat
     * 2. At the top of the screen when a new response comes in, following a query
     */
    newEl?.scrollIntoView({ behavior: 'smooth', block: oldEl ? 'start' : 'end' })
  })

  /**
   * When the last response grows too big to fit in the container, scroll to
   * the bottom of it so that the user can see new content as it comes in.
   */
  watch(lastResponseHeight, async (newHeight) => {
    if (!containerRef.value) return
    const heightLimit = containerRef.value.clientHeight - (inputRef.value?.clientHeight || 0)

    if (newHeight > heightLimit) {
      lastResponseRef.value?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' })
    }
  })

  return {
    chatPadding,
    inputPadding,
  }
}
